import { useCallback, useEffect, useRef, useState } from "react"

import type { RunsViewMode } from "../types/viewMode"

const HIGHLIGHT_DURATION_MS = 3000

interface SavedRunsSessionState {
  clickedRowId: string
  scrollOffset: number
  viewMode: RunsViewMode
}

interface RunsNavigationSessionState {
  clearSavedState: () => void
  getSavedState: () => SavedRunsSessionState | null
  saveBeforeNavigation: (params: {
    clickedRowId: string
    loadedCursors: (string | undefined)[]
    scrollOffset: number
    viewMode: RunsViewMode
  }) => void
}

interface UseRunsTableNavigationParams {
  buildHref: (path: string) => string
  isSelectMode: boolean
  loadedCursors: (string | undefined)[]
  navigate: (url: string) => void
  onRestoreViewMode: (viewMode: RunsViewMode) => void
  sessionState: RunsNavigationSessionState
  /** View named in the URL on load, which takes precedence over saved state. */
  urlViewMode?: RunsViewMode | null
  viewMode: RunsViewMode
}

export function useRunsTableNavigation({
  buildHref,
  isSelectMode,
  loadedCursors,
  navigate,
  onRestoreViewMode,
  sessionState,
  urlViewMode = null,
  viewMode,
}: UseRunsTableNavigationParams) {
  const scrollOffsetRef = useRef(0)
  const restoreViewModeRef = useRef(onRestoreViewMode)
  const currentViewModeRef = useRef(viewMode)
  const [highlightedRowId, setHighlightedRowId] = useState<string | null>(null)
  const [initialScrollOffset, setInitialScrollOffset] = useState(0)

  restoreViewModeRef.current = onRestoreViewMode
  currentViewModeRef.current = viewMode

  useEffect(() => {
    const savedState = sessionState.getSavedState()
    if (!savedState) {
      return
    }

    // A view named in the URL wins; state saved from the other view no longer applies.
    if (urlViewMode && urlViewMode !== savedState.viewMode) {
      sessionState.clearSavedState()
      return
    }

    if (savedState.viewMode !== currentViewModeRef.current) {
      restoreViewModeRef.current(savedState.viewMode)
    }

    setHighlightedRowId(savedState.clickedRowId)
    setInitialScrollOffset(savedState.scrollOffset)

    sessionState.clearSavedState()

    const highlightTimeoutId = window.setTimeout(() => {
      setHighlightedRowId(null)
    }, HIGHLIGHT_DURATION_MS)

    return () => {
      window.clearTimeout(highlightTimeoutId)
    }
  }, [sessionState, urlViewMode])

  const resetScrollPosition = useCallback(() => {
    scrollOffsetRef.current = 0
    setInitialScrollOffset(0)
  }, [])

  const handleScrollPositionChange = useCallback((offset: number) => {
    scrollOffsetRef.current = offset
  }, [])

  const navigateToUrl = useCallback(
    (clickedRowId: string, url: string) => {
      if (isSelectMode) {
        return
      }

      sessionState.saveBeforeNavigation({
        clickedRowId,
        scrollOffset: scrollOffsetRef.current,
        loadedCursors,
        viewMode,
      })
      navigate(url)
    },
    [isSelectMode, loadedCursors, navigate, sessionState, viewMode],
  )

  const runHref = useCallback(
    (runId: string) => buildHref(`/simulated/runs/${runId}`),
    [buildHref],
  )

  const simulationHref = useCallback(
    (runId: string, simulationOutputId: string) =>
      buildHref(`/simulated/runs/${runId}/results/${simulationOutputId}`),
    [buildHref],
  )

  const openRun = useCallback(
    (runId: string) => {
      navigateToUrl(runId, runHref(runId))
    },
    [navigateToUrl, runHref],
  )

  const openSimulation = useCallback(
    (runId: string, simulationOutputId: string) => {
      navigateToUrl(
        simulationOutputId,
        simulationHref(runId, simulationOutputId),
      )
    },
    [navigateToUrl, simulationHref],
  )

  const handleSimulate = useCallback(
    () => navigate(buildHref("/simulate")),
    [buildHref, navigate],
  )

  return {
    handleSimulate,
    handleRowClick: openRun,
    handleScrollPositionChange,
    handleSimulationRowClick: openSimulation,
    highlightedRowId,
    initialScrollOffset,
    openRun,
    openSimulation,
    resetScrollPosition,
    runHref,
    simulationHref,
  }
}
